import { Temporal } from '@js-temporal/polyfill';

/** Parse ISO date string (YYYY-MM-DD) to PlainDate, or null if invalid. */
export function parsePlainDate(dateStr: string): Temporal.PlainDate | null {
  const norm = normalizeIsoDate(dateStr);
  try {
    return Temporal.PlainDate.from(norm);
  } catch {
    return null;
  }
}

/** Normalize date string to ISO format (YYYY-MM-DD). */
export function normalizeIsoDate(dateStr: string): string {
  const m = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return dateStr;
  const [, y, month, day] = m;
  return `${y}-${month!.padStart(2, '0')}-${day!.padStart(2, '0')}`;
}

/** Format a PlainDate as YYYY-MM-DD. */
export function toIsoDateString(date: Temporal.PlainDate): string {
  return date.toString();
}

/** Today in the ISO calendar (local date). */
export function todayPlainDate(): Temporal.PlainDate {
  return Temporal.Now.plainDateISO();
}

/** Add days to a PlainDate. */
export function addDays(date: Temporal.PlainDate, days: number): Temporal.PlainDate {
  return date.add({ days });
}

/** All days from start to end inclusive. Returns [] if start > end. */
export function eachDayBetween(
  startIso: string,
  endIso: string
): Temporal.PlainDate[] {
  const start = parsePlainDate(normalizeIsoDate(startIso));
  const end = parsePlainDate(normalizeIsoDate(endIso));
  if (!start || !end || Temporal.PlainDate.compare(start, end) > 0) return [];
  const out: Temporal.PlainDate[] = [];
  let d = start;
  while (Temporal.PlainDate.compare(d, end) <= 0) {
    out.push(d);
    d = d.add({ days: 1 });
  }
  return out;
}

/** Compare two ISO date strings. Returns -1, 0, or 1. */
export function compareIsoDates(a: string, b: string): number {
  const pa = parsePlainDate(normalizeIsoDate(a));
  const pb = parsePlainDate(normalizeIsoDate(b));
  if (!pa || !pb) return 0;
  return Temporal.PlainDate.compare(pa, pb);
}

/** Format day date for display (e.g. "Wed, Feb 11"). */
export function formatDayDate(dateStr: string): string {
  const d = parsePlainDate(dateStr);
  if (!d) return dateStr;
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** Format trip date range without trailing year (e.g. "Feb 11 – Feb 18").
 *  Falls back to the year-bearing format when start/end span different years. */
export function formatTripDateRangeShort(startStr: string, endStr: string): string {
  const start = parsePlainDate(startStr);
  const end = parsePlainDate(endStr);
  if (!start || !end) return `${startStr} – ${endStr}`;
  if (start.year !== end.year) return formatTripDateRange(startStr, endStr);
  const opts = { month: 'short', day: 'numeric' } as const;
  return `${start.toLocaleString('en-US', opts)} – ${end.toLocaleString('en-US', opts)}`;
}

/** Format trip date range (e.g. "Feb 11 – Feb 18, 2026"). */
export function formatTripDateRange(startStr: string, endStr: string): string {
  const start = parsePlainDate(startStr);
  const end = parsePlainDate(endStr);
  if (!start || !end) return `${startStr} – ${endStr}`;
  const startFormatted = start.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const endFormatted = end.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return `${startFormatted} – ${endFormatted}`;
}

/** Add one day to a 'YYYY-MM-DD' string, returning the same format.
 *  Returns the input unchanged when it isn't a parseable date, so
 *  callers seeding defaults from user data can't crash on bad input. */
export function nextDayIso(ymd: string): string {
  const d = parsePlainDate(ymd);
  if (!d) return ymd;
  return toIsoDateString(d.add({ days: 1 }));
}

/** UI-friendly date label: "Today", "Tomorrow", "Yesterday", or "Wed, Feb 11". */
export function formatDateFriendly(
  dateStr: string,
  baseDate?: Temporal.PlainDate | undefined
): string {
  const d = parsePlainDate(dateStr);
  if (!d) return dateStr;
  const base = baseDate ?? todayPlainDate();
  const cmp = Temporal.PlainDate.compare(d, base);
  if (cmp === 0) return 'Today';
  if (cmp === 1 && Temporal.PlainDate.compare(d, base.add({ days: 1 })) === 0)
    return 'Tomorrow';
  if (cmp === -1 && Temporal.PlainDate.compare(d, base.subtract({ days: 1 })) === 0)
    return 'Yesterday';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
